import type { TemplateField } from "@/types/activity";

/**
 * Generate a JSON Schema from content_fields definition.
 */
export function generateContentSchema(fields: TemplateField[]): Record<string, unknown> {
  const properties: Record<string, unknown> = {};
  const required: string[] = [];

  for (const field of fields) {
    properties[field.name] = fieldToSchema(field);
    if (field.required) required.push(field.name);
  }

  return {
    type: "object",
    properties,
    ...(required.length > 0 ? { required } : {}),
  };
}

function fieldToSchema(field: TemplateField): Record<string, unknown> {
  const schema: Record<string, unknown> = {};

  switch (field.type) {
    case "string":
      schema.type = "string";
      if (field.format) schema.format = field.format;
      if (field.maximum !== undefined) schema.maxLength = field.maximum;
      break;
    case "integer":
      schema.type = "integer";
      if (field.minimum !== undefined) schema.minimum = field.minimum;
      if (field.maximum !== undefined) schema.maximum = field.maximum;
      break;
    case "boolean":
      schema.type = "boolean";
      break;
    case "select":
      schema.type = "string";
      if (field.options?.length) {
        schema.enum = field.options.map((opt) => (typeof opt === "string" ? opt : opt.key));
      }
      break;
    case "file":
      schema.type = "string";
      schema.format = "uri";
      break;
    case "audio":
      schema.type = "object";
      schema.properties = {
        url: { type: "string", format: "uri" },
        duration_seconds: { type: "number" },
      };
      schema.required = ["url"];
      break;
    case "video":
      schema.type = "string";
      schema.format = "uri";
      break;
    case "transcript":
      schema.type = "array";
      schema.items = { type: "string" };
      break;
    case "array":
      schema.type = "array";
      if (field.minItems !== undefined) schema.minItems = field.minItems;
      if (field.item) {
        if (field.item.type === "object" && field.item.fields) {
          const subProps: Record<string, unknown> = {};
          const subReq: string[] = [];
          for (const sf of field.item.fields) {
            subProps[sf.name] = fieldToSchema(sf);
            if (sf.required) subReq.push(sf.name);
          }
          if (field.hasTypology) {
            subProps.typology = { type: "string", enum: ["positive", "negative"] };
          }
          if (field.hasCustomItems) {
            subProps.is_custom = { type: "boolean" };
            subReq.push("is_custom");
          }
          schema.items = {
            type: "object",
            properties: subProps,
            ...(subReq.length > 0 ? { required: subReq } : {}),
          };
        } else {
          if (field.hasTypology) {
            schema.items = {
              type: "object",
              properties: {
                value: { type: field.item.type },
                typology: { type: "string", enum: ["positive", "negative"] },
              },
            };
          } else {
            schema.items = { type: field.item.type };
          }
        }
      } else {
        if (field.hasTypology) {
          schema.items = {
            type: "object",
            properties: {
              value: { type: "string" },
              typology: { type: "string", enum: ["positive", "negative"] },
            },
          };
        } else {
          schema.items = { type: "string" };
        }
      }
      break;
    case "score_points":
      schema.type = "object";
      schema.additionalProperties = { type: "integer" };
      break;
    default:
      schema.type = field.type;
  }

  return schema;
}
